import { Banner } from "@bb-ui/react-library/dist/components/Banner";
import type { PDFDocumentProxy } from "pdfjs-dist";
import React from "react";
import { useTranslation } from "react-i18next";

import { around, debounce } from "../../utils/utils";
import { DocumentViewerOptions } from "../document-viewer/document-viewer";
import { PdfPage } from "../pdf-page/pdf-page";
import { Toolbar } from "../toolbar/toolbar";

export type DocumentBodyProps = {
  /** The loaded and parsed PDF object*/
  pdf: PDFDocumentProxy;

  /** Whether the file required conversion or not */
  requiredConversion?: boolean;

  /** How long to debounce resize events by before re-scaling the PDF (in millis)  */
  resizeDelay?: number;

  /** The maximum number of pages that should be rendered when lazy-rendering pages */
  maxPagesToRender?: number;

  /** Additional options that control the document viewer's behaviour */
  viewerOptions: DocumentViewerOptions;

  /** Invoked when the user wants to download the original file */
  onDownloadOriginal: () => void;
};

export const DocumentBody: React.FC<DocumentBodyProps> = ({
  maxPagesToRender = 10,
  onDownloadOriginal,
  requiredConversion = false,
  resizeDelay = 250,
  pdf,
  viewerOptions,
}) => {
  const { t } = useTranslation();

  /** The number of pages to render before/after the current page */
  const pagesToRenderAroundCurrentPage =
    pdf.numPages < maxPagesToRender
      ? Math.round(pdf.numPages / 2)
      : Math.round(maxPagesToRender / 2);

  const containerRef = React.useRef(null);

  // The currently displayed page
  const [pageNumber, _setPageNumber] = React.useState(1);

  // Make the page number available through a reference as it needs to be accessed in a window event listener.
  // (see full screen logic).
  const pageNumberRef = React.useRef(pageNumber);
  function setPageNumber(n: number) {
    pageNumberRef.current = n;
    _setPageNumber(n);
  }

  // Whether the conversion banner (if any) has been dismissed
  const [isBannerDismissed, setBannerDismissed] = React.useState(false);

  // The scale at which the document is rendered
  const [scale, setScale] = React.useState(1);

  // The number of pages that have been rendered. This is used on initialization.
  // When all initial pages have been rendered, the scroll observation logic will be bound
  const [renderedPages, setRenderedPages] = React.useState(0);

  // Not all pages should be rendered to the DOM as that can be quite resource-intensive. Lazy-draw pages to a Canvas
  // as the user is scrolling through the document.
  const initialPagesToRender = Math.min(maxPagesToRender, pdf.numPages);
  const [visiblePages, setVisiblePages] = React.useState([
    ...Array(pdf.numPages)
      .fill(true, 0, initialPagesToRender)
      .fill(false, initialPagesToRender, pdf.numPages),
  ]);

  /** The intersection observed wrapped in react's state handling so it can be easily disconnected after unmounting */
  const [observer, setObserver] = React.useState<
    IntersectionObserver | undefined
  >(undefined);

  // Listen for window resize events and re-scale the PDF
  React.useEffect(() => {
    const resizeListener = debounce(() => calculateOptimalScale(pdf), {
      wait: resizeDelay,
    });
    window.addEventListener("resize", resizeListener);
    return () => window.removeEventListener("resize", resizeListener);
  }, [pdf, resizeDelay]);

  React.useEffect(() => {
    // Only do work when all the initial pages have rendered once.
    if (renderedPages !== pagesToRenderAroundCurrentPage * 2) {
      return;
    }

    /** This observer is called whenever pages cross the viewport. This can be both entering AND exiting. */
    function observerCallback() {
      // Determine which page is shown. It must be one of the visible ones
      const selectors = [...Array(pdf.numPages)].map(
        (v, pageIndex) => `[data-pdf-page-number="${pageIndex + 1}"]`,
      );
      const pageEls = [...document.querySelectorAll(selectors.join(", "))];
      const viewportHeight = document.documentElement.clientHeight;
      const visiblePages = pageEls
        .map((el) => {
          const rect = el.getBoundingClientRect();
          const visTop = Math.max(0, rect.top);
          const visBottom = Math.min(rect.bottom, viewportHeight);
          const visibleHeight = Math.max(visBottom - visTop);
          return { visibleHeight, el };
        })
        .filter(({ visibleHeight }) => visibleHeight > 0)
        .sort((a, b) => b.visibleHeight - a.visibleHeight);

      const visiblePage = visiblePages[0].el;
      if (visiblePage) {
        const visiblePageNumber = parseInt(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          visiblePage.getAttribute("data-pdf-page-number")!,
          10,
        );
        setPageNumber(visiblePageNumber);

        // Make the pages around the currently displayed page visible
        // Start off with an array that does not render any page. Pages around the threshold will be made visible
        const v = [...Array(pdf.numPages).fill(false, 0, pdf.numPages - 1)];
        const pageIndicesToShow = around(
          visiblePageNumber - 1,
          pagesToRenderAroundCurrentPage,
          pdf.numPages - 1,
        );
        pageIndicesToShow.forEach((pageIndex) => (v[pageIndex] = true));
        setVisiblePages(v);
      }
    }

    // TODO: Add Shim or scrollOffset workaround if IntersectionObserver is not available
    const options = {
      // Watch for changes on when pages become available in the viewport
      root: null,

      // The margin around the root that will be used to calculate where the threshold sits. A typical PDF page is
      // usually ~600px. By increasing this value we will more
      //rootMargin: '600px 0px',

      // The thresholds at which we want the observer to be fired. e.g. [0.25. 0.50] would trigger the observer when
      // a page becomes 25% or 50% visible. Keep in mind the callback fires both on scrolling up and down. i.e.
      // the callback fires when a page is scrolled up too.
      // The more thresholds are specified, the more intersection events will be observed.
      threshold: [0.01, 0.25, 0.5, 0.75, 0.99],
    };
    const obs = new IntersectionObserver(observerCallback, options);
    setObserver(obs);
    if (containerRef.current !== null) {
      [...Array(pdf.numPages)]
        .map((v, pageIndex) => `[data-pdf-page-number="${pageIndex + 1}"]`)
        .map((s) => document.querySelector(s))
        .forEach((el) => {
          if (el) {
            obs.observe(el);
          }
        });
    }
  }, [containerRef, pdf, renderedPages, pagesToRenderAroundCurrentPage]);

  // Remove the observer when the component unmounts
  React.useEffect(() => {
    return () => {
      if (observer !== undefined) {
        // Remove the old observer when the component unmounts. Note that this functions gets executed on every render..
        observer.disconnect();
      }
    };
  }, [observer]);

  // Determine what the base scale should be in order to fit the widest page in the available space
  React.useEffect(() => void calculateOptimalScale(pdf), [pdf]);

  /** Determine what the base scale should be in order to fit the widest page in the available space */
  async function calculateOptimalScale(pdf: PDFDocumentProxy) {
    // The available width in the view port
    const totalViewportWidth = document.documentElement.clientWidth;

    // Calculate the width of the widest page. Some pages might be landscape, others might be portrait
    const widths = await Promise.all(
      [...Array(pdf.numPages)].map((v, i) => {
        const pageNumber = i + 1;
        return pdf
          .getPage(pageNumber)
          .then((page) => page.getViewport({ scale: 1 }).width);
      }),
    );
    const maxWidth = Math.max.apply(null, widths);
    // Leave a bit of room for margin
    const requiredWidth = maxWidth + 40;
    if (totalViewportWidth > requiredWidth) {
      // If the viewport is large enough to accomodate the entire page at its intended size, do not adjust the scale.
      setScale(1);
    } else {
      // Otherwise, adjust the scale so that the widest page comfortable fits width-wise.
      const scale = totalViewportWidth / requiredWidth;
      setScale(scale);
    }
  }

  function goToPage(desiredPageNumber: number) {
    // Adjust the page number
    setPageNumber(desiredPageNumber);

    // Make the pages around the desired page visible
    const v = [...Array(pdf.numPages).fill(false, 0, pdf.numPages)];
    around(
      desiredPageNumber - 1,
      pagesToRenderAroundCurrentPage,
      pdf.numPages,
    ).forEach((i) => (v[i] = true));
    setVisiblePages(v);

    // Scroll to the page. The observation logic will make pages visible if required.
    document
      .querySelector(`[data-pdf-page-number="${desiredPageNumber}"]`)
      ?.scrollIntoView();
  }

  async function onFullScreenToggle(goFullscreen: boolean) {
    if (goFullscreen) {
      await document.querySelector(".document-body")?.requestFullscreen();
    } else {
      if (document.fullscreenElement !== null) {
        await document.exitFullscreen();
      }
    }
  }

  React.useEffect(() => {
    function onFullScreenChange() {
      // When going into full screen mode, the scroll offset will be reset and the user ends up at page 1.
      // Avoid that behaviour, and ensure the page the user was viewing is visible. There's no great hook to wait for
      // the scroll logic to complete, so just wait a half a second before going back to the initial page.
      // Note that this change listener gets fired both when clicking the button or ESCaping out of full screen mode
      const desiredPage = pageNumberRef.current;
      setTimeout(() => goToPage(desiredPage), 500);
    }

    document.addEventListener("fullscreenchange", onFullScreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullScreenChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf]);

  let className = "document-body";
  if (document.fullscreenElement !== null) {
    className += " fullscreen";
  }

  return (
    <div className={className} data-testid="document-body">
      <Toolbar
        pageNumber={pageNumber}
        numPages={pdf.numPages}
        scale={scale}
        viewerOptions={viewerOptions}
        onDownloadOriginal={onDownloadOriginal}
        onFullScreenToggle={onFullScreenToggle}
        onPageChange={goToPage}
        onScaleChange={setScale}
      ></Toolbar>
      <div className="page-container" ref={containerRef} role="main">
        {requiredConversion && !isBannerDismissed ? (
          <Banner
            severity="info"
            className="page-conversion-banner"
            onClose={() => setBannerDismissed(true)}
            closeText={t("messages.conversionWarning.dismiss")}
          >
            {t("messages.conversionWarning.message", viewerOptions)}
          </Banner>
        ) : undefined}
        {[...Array(pdf.numPages)].map((v, pageIndex) => {
          const visible = visiblePages[pageIndex] === true;
          return (
            <PdfPage
              key={`pdf_page_${pageIndex}`}
              onRenderSuccess={() => {
                setRenderedPages((prevState) => prevState + 1);
              }}
              pageNumber={pageIndex + 1}
              scale={scale}
              visible={visible}
            />
          );
        })}
      </div>
    </div>
  );
};
